/**
 * MIAirgapConfig — air-gapped / on-premise deployment hooks (§26).
 *
 * Lets operators redirect every external URL the SDK uses (basemap tiles,
 * 3D Tiles, terrain, sky/sun, fonts) to sovereign on-premise endpoints,
 * and load local DTED/3D-Tiles bundles from a single manifest.
 */

export interface MIAirgapEndpoints {
  basemapUrl?: string;
  tile3dRoot?: string;
  terrainProviderUrl?: string;
  sunPositionUrl?: string;
  glyphsUrl?: string;
  spriteUrl?: string;
  authTokenUrl?: string;
}

export interface MIOfflineBundleManifest {
  name: string;
  region: string;
  /** Absolute or relative URL of the bundle root directory. */
  rootUrl: string;
  /** Bundle content categories. */
  contains: ('basemap' | 'dted' | '3dtiles' | 'pointcloud' | 'fonts' | 'sprites')[];
  /** Optional per-category sub-paths beneath rootUrl. */
  paths?: Partial<Record<'basemap' | 'dted' | '3dtiles' | 'pointcloud' | 'fonts' | 'sprites', string>>;
  /** Optional version + checksum for verification. */
  version?: string;
  sha256?: string;
}

export interface MIAirgapConfigOptions {
  enabled?: boolean;
  endpoints?: MIAirgapEndpoints;
  /** Block any request that targets a non-allowlisted origin. */
  strict?: boolean;
  allowedOrigins?: string[];
  bundles?: MIOfflineBundleManifest[];
  /** Fallback baseUrl for any URL whose scheme is http(s) and is not allowlisted in strict mode. */
  proxyUrl?: string;
}

/**
 * Singleton air-gap configuration. Construct once at app start and reuse.
 */
export class MIAirgapConfig {
  private static instance: MIAirgapConfig | null = null;
  private opts: MIAirgapConfigOptions;
  private bundleIndex = new Map<string, MIOfflineBundleManifest>();

  static configure(options: MIAirgapConfigOptions): MIAirgapConfig {
    if (!MIAirgapConfig.instance) MIAirgapConfig.instance = new MIAirgapConfig(options);
    else MIAirgapConfig.instance.update(options);
    return MIAirgapConfig.instance;
  }

  static get(): MIAirgapConfig | null { return MIAirgapConfig.instance; }

  private constructor(options: MIAirgapConfigOptions) {
    this.opts = { enabled: true, strict: false, allowedOrigins: [], bundles: [], ...options };
    (this.opts.bundles ?? []).forEach((b) => this.bundleIndex.set(b.name, b));
  }

  update(options: Partial<MIAirgapConfigOptions>): void {
    this.opts = { ...this.opts, ...options };
    if (options.bundles) {
      this.bundleIndex.clear();
      options.bundles.forEach((b) => this.bundleIndex.set(b.name, b));
    }
  }

  isEnabled(): boolean { return !!this.opts.enabled; }

  /** Resolve a logical asset URL through air-gap rewrite rules. */
  resolve(kind: keyof MIAirgapEndpoints, requestedUrl: string): string {
    if (!this.isEnabled()) return requestedUrl;
    const override = this.opts.endpoints?.[kind];
    if (override) return override;
    if (this.opts.strict) {
      try {
        const u = new URL(requestedUrl, 'https://placeholder.local');
        const ok = (this.opts.allowedOrigins ?? []).some((origin) => u.origin === origin || u.host === origin);
        if (!ok && this.opts.proxyUrl) {
          return `${this.opts.proxyUrl.replace(/\/$/, '')}/${u.host}${u.pathname}${u.search}`;
        }
        if (!ok) throw new Error(`MIAirgapConfig: origin '${u.origin}' is not allowlisted in strict mode`);
      } catch (e) {
        if (e instanceof Error && e.message.startsWith('MIAirgapConfig')) throw e;
      }
    }
    return requestedUrl;
  }

  registerBundle(bundle: MIOfflineBundleManifest): void { this.bundleIndex.set(bundle.name, bundle); }
  getBundle(name: string): MIOfflineBundleManifest | undefined { return this.bundleIndex.get(name); }
  listBundles(): MIOfflineBundleManifest[] { return Array.from(this.bundleIndex.values()); }

  /** Resolve a path inside a registered offline bundle. */
  bundlePath(name: string, kind: 'basemap' | 'dted' | '3dtiles' | 'pointcloud' | 'fonts' | 'sprites', file = ''): string {
    const b = this.getBundle(name);
    if (!b) throw new Error(`MIAirgapConfig: bundle '${name}' not registered`);
    const sub = b.paths?.[kind] ?? kind;
    const root = b.rootUrl.replace(/\/$/, '');
    const path = file ? `${sub}/${file}` : sub;
    return `${root}/${path}`;
  }

  /**
   * Load a manifest from a relative URL (e.g. an offline tarball's index.json).
   * Useful in air-gapped browsers that can fetch from local Express servers.
   */
  async loadManifest(url: string): Promise<MIOfflineBundleManifest[]> {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`MIAirgapConfig: failed to load manifest ${url}: ${res.status}`);
    const data = await res.json();
    const list: MIOfflineBundleManifest[] = Array.isArray(data) ? data : Array.isArray(data?.bundles) ? data.bundles : [];
    list.forEach((b) => this.registerBundle(b));
    return list;
  }
}
